import { useEffect, useRef } from "react";

interface UseCloseOnScrollOptions {
  isOpen: boolean;
  onClose: () => void;
  shouldBlurElement?: boolean;
}

/**
 * Custom hook that closes a dropdown/popover when the user scrolls
 * @param isOpen - Boolean indicating if the dropdown is currently open
 * @param onClose - Callback function to close the dropdown
 * @param shouldBlurElement - Whether to blur the target element (useful for MUI Select components)
 * @returns ref - Ref to attach to the target element (required if shouldBlurElement is true)
 */
const useCloseOnScroll = <T extends HTMLElement = HTMLDivElement>({
  isOpen,
  onClose,
  shouldBlurElement = false
}: UseCloseOnScrollOptions) => {
  const elementRef = useRef<T>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        // Force close by blurring the element if requested
        if (shouldBlurElement && elementRef.current) {
          const focusableElement = elementRef.current.querySelector(
            "input, [role=\"combobox\"], button, [tabindex]"
          );
          if (focusableElement && "blur" in focusableElement) {
            (focusableElement as HTMLElement).blur();
          }
        }

        // Call the close callback
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener("scroll", handleScroll, { passive: true });
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isOpen, onClose, shouldBlurElement]);

  return elementRef;
};

export default useCloseOnScroll;